<script lang="ts">
    import { tweened } from "svelte/motion";
    import * as easings from "svelte/easing";
    import { CheckIcon } from "svelte-feather-icons";
    import {
        Answer,
        AnsweredQuestions,
        AnswerID,
        getDefaultAnswer,
        getPreferredResortComparisonQuestions,
        isResortComparisonQuestion,
        loadAnswersFromLocalStorage,
        Question,
        QuestionAnswer,
        QuestionCollection,
        QuestionGroup,
        QuestionID,
        QuestionSection,
        QuestionType,
        resortComparisonQuestions,
        storeAnswersInLocalStorage,
        toURLParamValue,
    } from "../questions";
    import QuestionComponent from "./QuestionComponent.svelte";
    import { navigate } from "../routing";
    import { search, SearchResponse } from "../search";
    import { LOCATION } from "../routing/contexts";
    import { getContext } from "svelte";
    import { fade, fly, FlyParams, TransitionConfig } from "svelte/transition";
    import LoadingSpinner from "./LoadingSpinner.svelte";
    import type { RouteLocation } from "../routing/route_location";
    import arrayShuffle from "array-shuffle";
    import { formatNumSlopes } from "../formatting";
    import { getPreviouslyVisitedResorts } from "../utils";

    export let location: RouteLocation;

    const user_id = 0;
    let question_collection: QuestionCollection | null = null;
    fetch("/questions")
        .then((x) => x.json())
        .then((x) => {
            question_collection = x as QuestionCollection;
            questions = question_collection.questions;
            structure = question_collection.structure;
            updateAnsweredQuestionsFromUrl(location.search);
            getResults();
            nextQuestion();
        });
    let questions: Question[] | null = null;
    let structure: QuestionSection[] | null = null;
    let previousAnswers: AnsweredQuestions | null = null;

    let answer: Answer | null = null;
    let question: Question | null = null;
    let answered = new AnsweredQuestions();

    let search_results_promise: SearchResponse | null = null;

    let progress = tweened(0, { duration: 300, easing: easings.cubicOut });
    let loadingFinalResult = false;

    const refreshUrl = (ans: AnsweredQuestions | null = null) => {
        if (ans == null) ans = answered;
        let path = location.pathname;
        const urlParams = ans.toURLParams(false, true);
        const newUrl = path + (ans ? "?" + urlParams.toString() : "");
        navigate(newUrl);
    };

    const updateAnsweredQuestionsFromUrl = (url: string) => {
        if (question_collection) {
            let newAnswered: AnsweredQuestions | null;
            newAnswered = AnsweredQuestions.fromURLParams(new URLSearchParams(url), question_collection);
            if (newAnswered && !newAnswered.equals(answered)) {
                answered = newAnswered;
                nextQuestion();
            }
        }
    };

    const findQuestionById = (id: number): Question | null => {
        if (questions == null) {
            return null;
        }
        const question = questions.find((q) => q.id == id);
        return question || null;
    };

    const getResults = () => {
        search(answered).then((x) => (search_results_promise = x));
    };

    const submitQuestion = () => {
        if (question === null) return;

        const questionAnswer: QuestionAnswer = {
            question_id: question.id,
            answer,
        };
        if (isResortComparisonQuestion(question.id) && question.type == QuestionType.Alternatives) {
            let alternatives = question.alternatives.map((a) => a.id.toString()).join(" ");
            fetch(
                `/rankresorts?qid=${question.id}&alternatives=${alternatives}&answer=${
                    toURLParamValue(questionAnswer) || "none"
                }`
            );
        }
        if (question.id == QuestionID.SingleResortRating && question.type == QuestionType.Alternatives) {
            const previouslyVisited = getPreviouslyVisitedResorts(answered, search_results_promise?.results || [])
                .map((a) => a.toString())
                .join(" ");
            let alternatives = question.alternatives.map((a) => a.id.toString()).join(" ");
            fetch(
                `/rankresort?resortid=${previouslyVisited}&alternatives=${alternatives}&answer=${
                    toURLParamValue(questionAnswer) || "none"
                }`
            );
        }

        if (plausible) {
            plausible("Answered question", { props: { question: question.header } });
        }
        answered.questions.push(questionAnswer);
        storeAnswersInLocalStorage(answered);
        refreshUrl();
        getResults();
    };

    const resortComparisonQuestionsPreferenceOrder: QuestionID[] = arrayShuffle(
        Array.from(resortComparisonQuestions.values())
    );

    const nextQuestion = () => {
        loadingFinalResult = false;

        const numberOfPreviouslyVisitedResorts = getPreviouslyVisitedResorts(
            answered,
            search_results_promise?.results || []
        ).length;
        if (structure != null) {
            const answeredQuestions: number[] = [];
            const remainingQuestions: { questionId: number; index: number }[] = [];
            const seenQuestionGroups: Set<QuestionGroup> = new Set();
            let totalQuestions = 0;
            for (let group of structure) {
                for (let child of group.children) {
                    const q = findQuestionById(child);
                    if (q) {
                        if (
                            q.question_group == QuestionGroup.SingleQuestion ||
                            !seenQuestionGroups.has(q.question_group)
                        ) {
                            totalQuestions += 1;
                            seenQuestionGroups.add(q.question_group);
                        }
                        if (answered.hasQuestionID(q.id)) {
                            const answer = answered.getByID(q.id).answer;
                            if (!answer || !answer.selected) {
                                continue;
                            }
                            if (answer.selected instanceof Set && answer.selected.size == 0) {
                                continue;
                            }
                            answeredQuestions.push(q.id);
                        } else {
                            remainingQuestions.push({ questionId: q.id, index: totalQuestions - 1 });
                        }
                    }
                }
            }
            const hasAnsweredAnyResortComparisonQuestion = answered.questions.some((q) =>
                resortComparisonQuestions.has(q.question_id)
            );
            const preferredResortComparisonQuestions = getPreferredResortComparisonQuestions(answered);
            const preferredResortComparisonQuestion = resortComparisonQuestionsPreferenceOrder.find((q) =>
                preferredResortComparisonQuestions.includes(q)
            );
            for (let q of remainingQuestions) {
                if (!answered.hasQuestionID(q.questionId)) {
                    const newQuestion = findQuestionById(q.questionId);
                    if (newQuestion === null) {
                        console.log(`Missing question ${q.questionId}`);
                    } else {
                        let missingPrerequisite = false;
                        for (let prerequisite of newQuestion.prerequisites) {
                            if (!answeredQuestions.includes(prerequisite)) {
                                missingPrerequisite = true;
                            }
                        }
                        // Don't ask about origin or travel time if the user selected "None of these" for transportation.
                        if (newQuestion.id == QuestionID.Origin || newQuestion.id == QuestionID.TravelTime) {
                            const transportation = answered.tryGetByID(QuestionID.Transportation)?.answer?.selected;
                            if (
                                !transportation ||
                                (transportation instanceof Set &&
                                    transportation.size == 1 &&
                                    transportation.values().next().value == AnswerID.NoneOfThese)
                            ) {
                                missingPrerequisite = true;
                            }
                        }
                        if (missingPrerequisite) {
                            continue;
                        }
                        if (isResortComparisonQuestion(newQuestion.id)) {
                            if (
                                newQuestion.id != preferredResortComparisonQuestion ||
                                hasAnsweredAnyResortComparisonQuestion ||
                                numberOfPreviouslyVisitedResorts <= 1
                            ) {
                                continue;
                            }
                        }
                        if (newQuestion.id == QuestionID.SingleResortRating) {
                            if (numberOfPreviouslyVisitedResorts != 1) {
                                continue;
                            }
                        }
                        if (
                            newQuestion.id == QuestionID.PreviouslyVisitedResorts &&
                            preferredResortComparisonQuestions.length == 0
                        ) {
                            // Not much point in asking where the user has been previously, since they don't seem to want enter any input anyway.
                            continue;
                        }
                        question = newQuestion;
                        progress.set((100 * q.index) / totalQuestions);
                        answer = getDefaultAnswer(question);
                        return;
                    }
                }
            }
        }

        progress.set(100);
        loadingFinalResult = true;
        question = null;
        setTimeout(() => {
            loadingFinalResult = false;
            storeAnswersInLocalStorage(answered);
            navigate("/sök?" + answered.toURLParams().toString(), { replace: true });
        }, 3000);
    };

    $: {
        updateAnsweredQuestionsFromUrl(location.search);
    }

    let currentLocation: SvelteStore<RouteLocation> = getContext(LOCATION);
    function noAnimationOnNavigate<T>(
        node: Element,
        params: { fn: (node: Element, params: T) => TransitionConfig; params: T }
    ): TransitionConfig {
        if (location.pathname != $currentLocation.pathname) return { duration: 0 };
        return params.fn(node, params.params);
    }
</script>

<div class="quiz">
    {#await question_collection}
        <!-- svelte-ignore empty-block -->
    {:then page_data}
        <div class="progressbar">
            <div class="progress" style={`width: ${$progress}%`} />
        </div>
        <!-- If the URL doesn't already contain some answers, then check if there are any answers in local storage. -->
        {#if previousAnswers !== null && previousAnswers.questions.length > 0 && answered.questions.length == 0}
            <div
                class="question"
                in:noAnimationOnNavigate={{ fn: fly, params: { x: 200, delay: 100, duration: 400 } }}
                out:noAnimationOnNavigate={{ fn: fly, params: { x: -200, delay: 0, duration: 400 } }}
            >
                <h2>Vill du använda tidigare svar?</h2>
                <p>
                    Vi ser att du redan har svarat på vissa frågor. Vill du göra en ny sökning eller vill du starta från
                    dina tidigare svar?
                </p>
                <div class="button-row">
                    <button
                        on:click={() => {
                            if (plausible) {
                                plausible("Reuse previous answers", { props: { event: "User confirmed" } });
                            }
                            if (previousAnswers !== null) {
                                let ans = previousAnswers;
                                refreshUrl(ans);
                            }
                        }}>Använd tidigare svar</button
                    >
                    <button
                        on:click={() => {
                            if (plausible) {
                                plausible("Reuse previous answers", { props: { event: "User declined" } });
                            }
                            previousAnswers = null;
                            storeAnswersInLocalStorage(null);
                        }}>Ny sökning</button
                    >
                </div>
            </div>
        {:else if question !== null && answer !== null}
            {#key question.id}
                <QuestionComponent
                    resorts={search_results_promise ? search_results_promise.results : []}
                    {question}
                    {answer}
                    {answered}
                    onChange={(newAnswer) => (answer = newAnswer)}
                    inTransition={(node, _) =>
                        noAnimationOnNavigate(node, { fn: fly, params: { x: 200, delay: 100, duration: 400 } })}
                    outTransition={(node, _) =>
                        noAnimationOnNavigate(node, { fn: fly, params: { x: -200, delay: 0, duration: 400 } })}
                />
            {/key}
            <div
                class="question-next"
                in:fade={{ duration: 400 }}
                out:noAnimationOnNavigate={{ fn: fly, params: { duration: 400 } }}
                on:click={() => {
                    submitQuestion();
                    nextQuestion();
                }}
            >
                Nästa
            </div>
        {:else if loadingFinalResult}
            <div class="search-progress" in:fade={{ duration: 200 }}>
                <LoadingSpinner />
                <span>Söker efter de bästa skidorterna för dig...</span>
            </div>
        {/if}
    {:catch e}
        <p>Failed to load page</p>
        <p>{e}</p>
    {/await}
</div>
